import { useState } from "react";
import { Link, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Box, Container, Stack, Typography, Button, CircularProgress, Menu, MenuItem } from "@mui/material";
import { Logout as LogoutIcon, KeyboardArrowDown } from "@mui/icons-material";

import EventsPage from "./pages/Events";
import EPGPage from "./pages/EPG";
import PoliciesPage from "./pages/Policies";
import AudiencesPage from "./pages/Audiences";
import Header from "./components/Header";
import Docs from "./pages/Docs";
import AuditViewer from "./pages/Audit";
import DecisionTester from "./pages/DecisionTester";
import ScteGraph from "./pages/admin/ScteGraph";
import LoginPage from "./pages/Login";
import ChangePassword from "./pages/ChangePassword";
import ChannelsAdmin from "./pages/admin/Channels";
import GroupsAdmin from "./pages/admin/Groups";
import UsersAdmin from "./pages/admin/Users";
import { AuthProvider, useAuth } from "./auth/AuthContext";

type NavItem = { label: string; to: string; need?: "any" | "super" };

const NAV_GROUPS: { label: string; items: NavItem[] }[] = [
  {
    label: "Schedule",
    items: [
      { label: "EPG", to: "/epg" },
      { label: "Events", to: "/events" },
      { label: "Policy Builder", to: "/policies" },
      { label: "Audiences", to: "/audiences" },
    ],
  },
  {
    label: "Engine",
    items: [
      { label: "Decision Tester", to: "/decision-tester" },
      { label: "Audit", to: "/audit" },
    ],
  },
  {
    label: "Admin",
    items: [
      { label: "SCTE Graph", to: "/admin/scte", need: "any" },
      { label: "Channels", to: "/admin/channels", need: "any" },
      { label: "Users", to: "/admin/users", need: "any" },
      { label: "Groups", to: "/admin/groups", need: "super" },
    ],
  },
];

function routeActive(pathname: string, to: string) {
  return pathname === to || pathname.startsWith(to + "/");
}

function NavMenu({ label, items }: { label: string; items: NavItem[] }) {
  const { pathname } = useLocation();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const active = items.some((i) => routeActive(pathname, i.to));
  return (
    <>
      <Button
        color="inherit"
        endIcon={<KeyboardArrowDown />}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ color: active ? "primary.main" : "inherit", whiteSpace: "nowrap" }}
      >
        {label}
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {items.map((i) => (
          <MenuItem
            key={i.to}
            component={Link}
            to={i.to}
            selected={routeActive(pathname, i.to)}
            onClick={() => setAnchor(null)}
          >
            {i.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function NavTabs() {
  const { pathname } = useLocation();
  const { isAnyAdmin, isSuperAdmin } = useAuth();
  const allow = (need?: "any" | "super") =>
    !need || (need === "super" ? isSuperAdmin : isAnyAdmin);
  return (
    <Stack direction="row" alignItems="center" gap={0.5}>
      {NAV_GROUPS.map((g) => {
        const items = g.items.filter((i) => allow(i.need));
        if (!items.length) return null;
        return <NavMenu key={g.label} label={g.label} items={items} />;
      })}
      <Button
        component={Link}
        to="/docs"
        color="inherit"
        sx={{ color: routeActive(pathname, "/docs") ? "primary.main" : "inherit" }}
      >
        Docs
      </Button>
    </Stack>
  );
}

function UserMenu() {
  const { username, isSuperAdmin, logout } = useAuth();
  if (!username) return null;
  return (
    <Stack direction="row" alignItems="center" gap={1} sx={{ ml: 1 }}>
      <Typography variant="body2" sx={{ opacity: 0.85, whiteSpace: "nowrap" }}>
        {username}{isSuperAdmin ? " (admin)" : ""}
      </Typography>
      <Button size="small" color="inherit" variant="outlined" startIcon={<LogoutIcon />} onClick={logout}>
        Sign out
      </Button>
    </Stack>
  );
}

function AdminGuard({ need, children }: { need: "any" | "super"; children: React.ReactNode }) {
  const { isAnyAdmin, isSuperAdmin } = useAuth();
  const ok = need === "super" ? isSuperAdmin : isAnyAdmin;
  if (!ok) return <Navigate to="/events" replace />;
  return <>{children}</>;
}

function AppShell() {
  const { pathname } = useLocation();
  const isEpg = pathname.startsWith("/epg");
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", color: "text.primary" }}>
      <Header right={<Stack direction="row" alignItems="center"><NavTabs /><UserMenu /></Stack>} />
      <Container maxWidth={isEpg ? false : "lg"} sx={{ py: 3 }}>
        <Routes>
          <Route path="/" element={<EventsPage />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/epg" element={<EPGPage />} />
          <Route path="/epg/:channel" element={<EPGPage />} />
          <Route path="/policies" element={<PoliciesPage />} />
          <Route path="/audiences" element={<AudiencesPage />} />
          <Route path="/docs" element={<Docs />} />
          <Route path="/audit" element={<AuditViewer />} />
          <Route path="/decision-tester" element={<DecisionTester />} />
          <Route path="/admin/channels" element={<AdminGuard need="any"><ChannelsAdmin /></AdminGuard>} />
          <Route path="/admin/groups" element={<AdminGuard need="super"><GroupsAdmin /></AdminGuard>} />
          <Route path="/admin/users" element={<AdminGuard need="any"><UsersAdmin /></AdminGuard>} />
          <Route path="/admin/scte" element={<AdminGuard need="any"><ScteGraph /></AdminGuard>} />
          <Route path="*" element={<EventsPage />} />
        </Routes>
      </Container>
    </Box>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { authed, loading, mustChange } = useAuth();
  const { pathname } = useLocation();
  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", bgcolor: "background.default" }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!authed) return <Navigate to="/login" replace state={{ from: pathname }} />;
  if (mustChange && pathname !== "/change-password") return <Navigate to="/change-password" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/change-password" element={<RequireAuth><ChangePassword /></RequireAuth>} />
        <Route path="/*" element={<RequireAuth><AppShell /></RequireAuth>} />
      </Routes>
    </AuthProvider>
  );
}
